import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";
import { AuthMenu } from "@/components/site/AuthMenu";

export function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const { t } = useLang();
    const { user, logout } = useAuth();

    const links = [
        { to: "/", label: t.nav.home },
        { to: "/products", label: t.nav.devices },
        { to: "/about", label: t.nav.about },
        { to: "/contact", label: t.nav.contact },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-[background,border,box-shadow] duration-500 ${
                scrolled ? "glass border-b border-border shadow-[0_1px_0_rgba(0,0,0,0.02)]" : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
                <Link to="/" data-testid="logo-link" className="flex items-center group">
                    <img src="/korhc-logo.png?v=4" alt="KORHC" className="h-10 w-auto group-hover:opacity-80 transition-opacity duration-300" />
                </Link>

                <nav className="hidden md:flex items-center gap-9">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                            className={({ isActive }) =>
                                `eyebrow transition-colors duration-200 ${isActive ? "text-cyan" : "text-obsidian/60 hover:text-obsidian"}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <LanguageSwitcher testid="language-switcher" />
                    <AuthMenu />
                </div>

                <div className="md:hidden flex items-center gap-3">
                    <LanguageSwitcher testid="language-switcher-mobile" />
                    <button data-testid="mobile-menu-toggle" className="text-obsidian" onClick={() => setOpen((v) => !v)} aria-label="Menu">
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="md:hidden glass border-t border-border px-6 py-6 flex flex-col gap-5">
                    {links.map((l) => (
                        <NavLink key={l.to} to={l.to} end={l.to === "/"} className="eyebrow text-obsidian/70">
                            {l.label}
                        </NavLink>
                    ))}
                    {user === null ? null : !user ? (
                        <Link to="/login" data-testid="mobile-signin" className="eyebrow rounded-full bg-obsidian text-white px-5 py-3 text-center">
                            {t.auth.signIn}
                        </Link>
                    ) : (
                        <>
                            <div className="eyebrow text-cyan">@{user.username || user.name}</div>
                            {user.role === "admin" && <NavLink to="/admin" className="eyebrow text-obsidian/70">{t.auth.dashboard}</NavLink>}
                            <button data-testid="mobile-logout" onClick={async () => { await logout(); }} className="eyebrow rounded-full border border-border text-obsidian px-5 py-3 text-center">
                                {t.auth.logout}
                            </button>
                        </>
                    )}
                </div>
            )}
        </header>
    );
}
